import moment from 'moment-timezone';

export const getStartAndEndDate = ({
  date, 
  calendarView, 
  timezone = moment.tz.guess()
}) => {
  if (calendarView === 'day') {
    return {
      startDate: moment(date).tz(timezone).startOf('day').toISOString(),
      endDate: moment(date).tz(timezone).endOf('day').toISOString(),
    };
  }
  // default to week view
  return {
    startDate: moment(date).tz(timezone).startOf('week').toISOString(),
    endDate: moment(date).tz(timezone).endOf('week').toISOString(),
  };
};

const dateToDateKey = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

/**
 * Returns the position style for an appointment in the week grid.
 * weekDates: [Date] length 7 (Sun-Sat)
 * appointment: { start: Date|string, end: Date|string }
 * Returns { top, left, width, height } in pixels, or null if appointment is outside this week or outside 8am-8pm.
 */
export const getWeeklyAppointmentStyle = ({
  session, 
  timezone = moment.tz.guess(),
  weekDates, 
  hideWeekends = false,
  cellWidth = 150,
  hourHeight = 80,
  startHour = 8,
  endHour = 20,
}) => {
  const START_MINUTES = startHour * 60;
  const END_MINUTES = endHour * 60;
  const start = (
    session?.date instanceof Date 
    ? session.date
    : new Date(session.date)
  );
  const duration = session?.duration || 60; // default to 60 minutes if no duration provided
  const end = (
    session?.end instanceof Date
    ? session.end
    : new Date(start.getTime() + duration * 60000)
  );
  const _weekDates = (
    hideWeekends 
    ? weekDates.filter((d, i) => ![0,6].includes(d.getDay())) 
    : weekDates
  );
  
  const startKey = dateToDateKey(start);
  const dayIndex = _weekDates.findIndex((d) => dateToDateKey(d) === startKey);
  if (dayIndex < 0) return null;
  const startInTZ = moment(start).tz(timezone || moment.tz.guess());
  const endInTZ = moment(end).tz(timezone || moment.tz.guess());
  const startMinutes = startInTZ.hours() * 60 + startInTZ.minutes();
  const endMinutes = endInTZ.hours() * 60 + endInTZ.minutes();

  // Clip to visible range 8am–8pm
  let topMinutes = startMinutes - START_MINUTES;
  let endMinutesClipped = Math.min(endMinutes, END_MINUTES);
  let startMinutesClipped = Math.max(startMinutes, START_MINUTES);
  let durationMinutes = endMinutesClipped - startMinutesClipped;
  if (durationMinutes <= 0) return null;

  topMinutes = startMinutesClipped - START_MINUTES;

  const top = (topMinutes / 60) * hourHeight;
  const height = (durationMinutes / 60) * hourHeight;
  const left = cellWidth + dayIndex * cellWidth;
  const width = cellWidth;

  return { top, left, width, height };
};

export const getDailyAppointmentStyle = ({
  session,
  timezone,
  hourWidth = 80,
  cellWidth = 150,
  hourHeight = 80,
  startHour = 8,
  endHour = 20,
}) => {
  const START_MINUTES = startHour * 60;
  const END_MINUTES = endHour * 60;
  const start = (
    session?.date instanceof Date 
    ? session.date
    : new Date(session.date)
  );
  const duration = session?.duration || 60; // default to 60 minutes if no duration provided
  const end = (
    session?.end instanceof Date
    ? session.end
    : new Date(start.getTime() + duration * 60000)
  );
  const startInTZ = moment(start).tz(timezone || moment.tz.guess());
  const endInTZ = moment(end).tz(timezone || moment.tz.guess());
  const startMinutes = startInTZ.hours() * 60 + startInTZ.minutes();
  const endMinutes = endInTZ.hours() * 60 + endInTZ.minutes();

  // Clip to visible range 8am–8pm
  let topMinutes = startMinutes - START_MINUTES;
  let endMinutesClipped = Math.min(endMinutes, END_MINUTES);
  let startMinutesClipped = Math.max(startMinutes, START_MINUTES);
  let durationMinutes = endMinutesClipped - startMinutesClipped;
  if (durationMinutes <= 0) return null;

  topMinutes = startMinutesClipped - START_MINUTES;

  const top = (topMinutes / 60) * hourHeight;
  const height = (durationMinutes / 60) * hourHeight;
  const left = hourWidth;
  const width = cellWidth;

  return { top, left, width, height };
};

/**
 * Get the Sunday of the week that contains the given date.
 */
export const getWeekStart = (date) => {
  const d = date instanceof Date ? new Date(date) : new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  const sunday = new Date(d);
  sunday.setDate(diff);
  sunday.setHours(0, 0, 0, 0);
  return sunday;
};

/**
 * Get array of 7 dates (Sun–Sat) for the week that contains the given date.
 */
export const getWeekDates = (date) => {
  const start = getWeekStart(date);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

export const getTimeBlocksForRepeatingEvent = ({
  startHour, 
  endHour, 
  timeStart,
}) => event => {
  const startOfWeek = moment(timeStart).startOf('week');
  const startTime = moment(event.start_time);
  const recurringDays = event.recurring_days || [];
  if (!recurringDays.length) return [];
  const recurringStartTime = event.recurring_start_time; // "13:00" 24 hour time
  const recurringEndTime = event.recurring_end_time; // "17:00" 24 hour time
  const repeatEveryNWeeks = event.repeat_every_period_value;
  // check if this week is one of the active weeks for this recurring vacation time
  const weeksSinceStart = startOfWeek.diff(startTime, 'weeks');
  console.log('getTimeBlocksForRepeatingEvent', {startOfWeek, startTime, recurringDays, recurringStartTime, recurringEndTime, repeatEveryNWeeks, weeksSinceStart});
  if (weeksSinceStart % repeatEveryNWeeks !== 0) return [];
  const timeBlocks = recurringDays.map(day => {
    const dayIndex = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].indexOf(
      day.toLowerCase()
    );
    const blockStart = startOfWeek.clone().add(dayIndex, 'days').add(recurringStartTime, 'hours');
    const earliestStart = startOfWeek.clone().add(dayIndex, 'days').add(startHour, 'hours');
    if (blockStart.isBefore(earliestStart)) blockStart.set({ hour: startHour, minute: 0 });
    const blockEnd = startOfWeek.clone().add(dayIndex, 'days').add(recurringEndTime, 'hours');
    const latestBlockEnd = startOfWeek.clone().add(dayIndex, 'days').add(endHour, 'hours');
    if (blockEnd.isAfter(latestBlockEnd)) blockEnd.set({ hour: endHour, minute: 0 });
    return {
      event,
      date: blockStart.toISOString(),
      start: blockStart.toISOString(),
      end: blockEnd.toISOString(),
      duration: blockEnd.diff(blockStart, 'minutes'),
      isAwayTime: event.category === 'AWAY_TIME',
    };
  });
  return timeBlocks;
};

const getTimeBlocksForMultiDayEvent = ({startHour, endHour}) => event => {
  let start = moment(event.start_time);
  const end = moment(event.end_time);
  const numDays = end.diff(start, 'days') + 1;
  const blocks = Array.from({ length: numDays }, (_, i) => {
    const blockStart = i === 0 ? start : moment(start).add(i, 'days').startOf('day');
    const earliestBlockStart = moment(blockStart).set({ hour: startHour, minute: 0 });
    if (blockStart.isBefore(earliestBlockStart)) blockStart.set({ hour: startHour, minute: 0 });
    const isLastDay = i === numDays - 1;
    const blockEnd = isLastDay ? end : moment(start).add(i, 'days').endOf('day');
    const latestBlockEnd = moment(blockEnd).set({ hour: endHour, minute: 0 });
    if (blockEnd.isAfter(latestBlockEnd)) blockEnd.set({ hour: endHour, minute: 0 });
    return {
      event,
      date: blockStart.toISOString(),
      start: blockStart.toISOString(),
      end: blockEnd.toISOString(),
      duration: blockEnd.diff(blockStart, 'minutes'),
      isAwayTime: event.category === 'AWAY_TIME',
    };
  });
  return blocks;
};

const getTimeBlocksForSingleDayEvent = ({startHour, endHour}) => event => {
  const start = moment(event.start_time);
  const end = moment(event.end_time);
  // const earliestStart = moment(start).set({ hour: startHour, minute: 0 });
  // if (start.isBefore(earliestStart)) start.set({ hour: startHour, minute: 0 });
  // const latestEnd = moment(end).set({ hour: endHour, minute: 0 });
  // if (end.isAfter(latestEnd)) end.set({ hour: endHour, minute: 0 });
  return [{
    event,
    date: start.toISOString(),
    start: start.toISOString(),
    end: end.toISOString(),
    duration: end.diff(start, 'minutes'),
    isAwayTime: event.category === 'AWAY_TIME',
  }];
}

export const getAvailabilityBlocks = ({
  date,
  startHour = 8,
  endHour = 20,
  timezone = moment.tz.guess(),
}) => availabilities => {
  const weekStart = moment(date).startOf('week');
  const DAY_ORDER = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  return availabilities.flatMap(avail => {
    const { day_availabilities = [], css_color, name, date_range_start, date_range_end } = avail;
    return day_availabilities.flatMap(({ day, start_minute, end_minute }) => {
      const dayIndex = DAY_ORDER.indexOf((day || day?.day || '').toUpperCase());
      if (dayIndex < 0) return [];
      const blockDate = moment(weekStart).tz(timezone).add(dayIndex, 'days');
      if (date_range_start && blockDate.isBefore(moment(date_range_start), 'day')) return [];
      if (date_range_end && blockDate.isAfter(moment(date_range_end), 'day')) return [];
      const visibleStart = Math.max(start_minute, startHour * 60);
      const visibleEnd = Math.min(end_minute, endHour * 60);
      if (visibleEnd <= visibleStart) return [];
      const blockStart = moment(blockDate).tz(timezone).startOf('day').add(visibleStart, 'minutes');
      const blockEnd = moment(blockDate).tz(timezone).startOf('day').add(visibleEnd, 'minutes');
      return [{
        id: avail.id,
        title: avail.title,
        date: blockStart.toISOString(),
        start: blockStart.toISOString(),
        end: blockEnd.toISOString(),
        duration: blockEnd.diff(blockStart, 'minutes'),
        name,
        css_color,
        availability: avail,
        isAvailabilityBlock: true,
      }];
    });
  });
};

export const getEventBlocks = ({
  timeStart, 
  timeEnd,
  startHour,
  endHour,
}) => events => (
  events
    .filter(event => {
      if (moment(event.start_time).isAfter(moment(timeEnd))) return false;
      if (
        event.end_time 
        && moment(event.end_time).isBefore(moment(timeStart))
      ) {
        return false;
      }
      return true;
    })
    .flatMap(event => {
      if (event.is_recurring) {
        const blocks = getTimeBlocksForRepeatingEvent({
          startHour, 
          endHour, 
          timeStart,
        })(event);
        return blocks;
      }
      const isMultiDay = !moment(event.start_time).isSame(moment(event.end_time), 'day');
      if (isMultiDay) console.log('isMULTIDAY', event, isMultiDay);
      if (isMultiDay) return getTimeBlocksForMultiDayEvent({startHour, endHour})(event);
      const blocks = getTimeBlocksForSingleDayEvent({startHour, endHour})(event);
      console.log('single day block', blocks, moment(blocks[0].start).tz('America/Detroit').format('hh:mm a YYYY-MM-DD'));
      return blocks;
    })
);
